import React from 'react';

import type { SelectOption } from '@rescui/select/lib/types';

import { UserRole } from '@/enums/users';
import { StudentStatus } from '@/types/users';
import type { UserModelType } from '@/models/users';
import type {
  ClassifierOption,
  ClassifierOptionsResponse,
  LabelValueOption,
} from '@/types/shared';

export const isServer = (): boolean => {
  return typeof window === 'undefined';
};

export const isDevelopment = (): boolean => {
  return process.env.NODE_ENV === 'development';
};

export const isTest = (): boolean => {
  return !!process.env.NEXT_PUBLIC_TESTING || process.env.NODE_ENV === 'test';
};

export const isDebug = (): boolean => {
  return !!process.env.NEXT_PUBLIC_UI_DEBUG_FEATURES_ENABLED;
};

export const toOption = (o: ClassifierOption): SelectOption => {
  return {
    label: o.label,
    value: o.id,
  };
};

export const mapOptions = (
  data?: ClassifierOptionsResponse,
): LabelValueOption[] => {
  return data?.classifierOptions.map(toOption) || [];
};

export const capitalizeFirstLetter = (str: string | undefined) => {
  if (!str) {
    return;
  }

  return str.charAt(0).toUpperCase() + str.toLowerCase().slice(1);
};

export const getStudentStatus = (
  user: UserModelType | undefined,
): StudentStatus => {
  if (!user) {
    return StudentStatus.NOT_FOUND;
  }

  if (user.role !== UserRole.STUDENT) {
    return StudentStatus.NOT_FOUND;
  }
  if (user.studentprofile) {
    if (user.studentprofile.status === 'PARTIALLY_FILLED') {
      return StudentStatus.REGISTERED;
    } else if (user.studentprofile.status === 'ACTIVE') {
      return StudentStatus.ACTIVE;
    }
    return StudentStatus.BANNED;
  } else {
    return StudentStatus.ONLY_EMAIL;
  }
};

/**
 * Some links (e.g. portfolio link in student profile) may not contain http/https schema,
 * in this case incorrect URL would be formed. Add `//` prefix if no schema specified.
 */
export const prefixUrl = (url: string | undefined): string => {
  if (!url) {
    return '#';
  }

  return url.startsWith('http') ? url : `//${url}`;
};

export const sum = (values: number[]) => {
  return values.reduce((a, b) => a + b, 0);
};

export const isRSC = () => {
  return !('useEffect' in React);
};

export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));
